'use client';

import { useMemo, useState } from 'react';

import { type AboutContent, type ApplicationCategory } from '@api/applications/general';

import { cn } from '@ui/uikit/utils';

import TruncTextWithReadMore from '../../TruncTextWithReadMore/internals/TruncTextWithReadMore';
import styles from './AboutTabs.module.scss';

interface TabContent {
  id: string;
  label: string;
  content: string;
}

type AboutTabsContent = Omit<AboutContent, 'token_overview' | 'token_summary' | 'token_whitepaper'>;

interface AboutTabsProps {
  about?: AboutTabsContent;
  category?: ApplicationCategory;
}

const AboutTabs = ({ about, category }: AboutTabsProps) => {
  const [activeTab, setActiveTab] = useState('introduction');

  const tabs: TabContent[] = useMemo(() => {
    return [
      {
        id: 'introduction',
        label: 'Introduction',
        content: about?.introduction || '',
      },
      {
        id: 'overview',
        label: 'Overview',
        content: about?.overview || '',
      },
      {
        id: 'benefits',
        label: category?.category_name === 'Games' ? 'Gameplay' : 'Benefits & Features',
        content: about?.benefits || '',
      },
      {
        id: 'get_started',
        label: 'Get Started',
        content: about?.get_started || '',
      },
      {
        id: 'project_vision',
        label: 'Project Vision',
        content: about?.project_vision || '',
      },
      {
        id: 'roadmap',
        label: 'Roadmap',
        content: about?.roadmap || '',
      },
      {
        id: 'meet_the_team',
        label: 'Team & Investors',
        content: about?.meet_the_team || '',
      },
      {
        id: 'testnet_beta_access',
        label: 'Early Access',
        content: about?.testnet_beta_access || '',
      },
    ].filter((tab) => tab.content);
  }, [about, category?.category_name]);

  return (
    <div className={styles['container']}>
      <nav className={styles['tabs']}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(styles['tabs__tab'], {
              [styles['tabs__tab_active']]: activeTab === tab.id,
            })}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {tabs.map((tab) => (
        <div
          key={tab.id}
          className={cn({
            [styles['tabText_visible']]: activeTab === tab.id,
            [styles['tabText_hidden']]: activeTab !== tab.id,
          })}
        >
          <TruncTextWithReadMore text={tab.content} />
        </div>
      ))}
    </div>
  );
};

export { AboutTabs };
